import React, {useCallback, useEffect, useState, useContext, useRef} from 'react';
import {Snackbar, Toast, confirm, setOptions, alert, toast as mbToast, Button} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import './styles/dashboard.css';
import {Chart as ChartJS, CategoryScale, LinearScale, ArcElement, BarElement,  PointElement, LineElement, Title, Tooltip, Legend, Filler, PolarAreaController, RadialLinearScale} from 'chart.js';
import DashboardHeader from "./blocks/DashboardHeader";
import DashboardCalendar from './blocks/DashboardCalendar';
import DashboardWorkspace from './blocks/DashboardWorkspace';
import DashboardQuestion from './blocks/DashboardQuestion';
import DashboardConversation from './blocks/DashboardConversation';
import DashboardThinking from './blocks/DashboardThinking';
import DashboardWorkspaceGraph from "./blocks/DashboardWorkspaceGraph";
import DashboardWorkspaceDoc from "./blocks/DashboardWorkspaceDoc";
import DashboardWorkspaceText from "./blocks/DashboardWorkspaceText";
import DashboardWorkspaceCode from "./blocks/DashboardWorkspaceCode";
import DashboardWorkspaceApp from "./blocks/DashboardWorkspaceApp";
import DashboardWorkspaceImage from "./blocks/DashboardWorkspaceImage";
import DashboardWorkspaceRename from "./dialogs/DashboardWorkspaceRename";
import DashboardWorkspaceAddName from "./dialogs/DashboardWorkspaceAddName";
import DashboardItemsRename from "./dialogs/DashboardItemsRename";
import DashboardWorkspaceGenerate from "./dialogs/DashboardWorkspaceGenerate"
import DashboardWorkspaceEvent from "./dialogs/DashboardWorkspaceEvent"
import Empty from './utils/Empty';
import Loading from "./utils/Loading";
import {NetworkContext} from "./utils/NetworkContext";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, PolarAreaController, RadialLinearScale);

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function Dashboard({wss, width, locale, configuration, dialog, Dialog, Navigation, navigation, setConfiguration, setLocale, token, account, session}) {
    const {wsCall, wsResponse, setWsResponse} = useContext(NetworkContext);
    const hiddenFileInput = useRef(null);
    const [snackbar, setSnackbar] = useState({});
    const [functions, setFunctions] = useState([]);
    const [system, setSystem] = useState(false);
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [workspace, setWorkspace] = useState([]);
    const [stack, setStack] = useState([]);
    const [workspaceItem, setWorkspaceItem] = useState({});
    const [workspaceItemDialog, setWorkspaceItemDialog] = useState({});
    const [myItem, setMyItem] = useState({});
    const [ui, setUi] = useState({});
    const [text, setText] = useState('<p></p>');
    const [calendar, setCalendar] = useState([]);
    const [model, setModel] = useState(null);
    const [label, setLabel] = useState(null);
    const [avatar, setAvatar] = useState(null);
    const [teamtalk, setTeamtalk] = useState({});
    const [user, setUser] = useState({});
    const [toast, setToast] = useState({});
    const [broadcast, setBroadcast] = useState('');
    const [viewConversation, setViewConversation] = useState(false);
    const [viewCompressed, setViewCompressed] = useState(false);
    const [thinking, setThinking] = useState(null);
    const [voice, setVoice] = useState(null);
    const [tokensUsed, setTokensUsed] = useState(0);
    const [folded, setFolded] = useState(messages !== 0);
    const [sort, setSort] = useState(true);
    const [locked, setLocked] = useState(false);
    const [capturing, setCapturing] = useState(false);

    const Start = () => {
        setToast({message: locale.dashboard.a, color: 'info', display: 'bottom', duration: 1000});

        const message = {
            type: 'wss',
            path: 'user-login',
            action: 'get',
            data: {}
        };
        wsCall(message);
    }

    const Locale = data => {
        if (data === 'es' && data !== window.localStorage.locale) {
            setLocale(configuration.locale.es);
            window.localStorage.locale = 'es';
        } else if (data === 'en' && data !== window.localStorage.locale) {
            setLocale(configuration.locale.en);
            window.localStorage.locale = 'en';
        }
    }

    const Conversation = () => {
        setToast({message: locale.dashboard.b, color: 'info', display: 'bottom', duration: 1000});

        const message = {
            type: 'wss',
            path: 'conversation',
            action: 'get',
            data: {
                system: system,
                rewind: false,
                error: false
            }
        };
        wsCall(message);
    }

    const Delete = data => {
        confirm({
            title: locale.dashboard.j,
            message: locale.dashboard.k,
            okText: locale.dashboard.l,
            cancelText: locale.dashboard.m,
            callback: (res) => {
                if (res) {
                    setToast({message: locale.dashboard.b, color: 'info', display: 'bottom', duration: 1000});

                    const message = {
                        type: 'wss',
                        path: 'conversation-item',
                        action: 'delete',
                        data: {
                            item: data,
                            system: system
                        }
                    };
                    wsCall(message);
                }
            }
        });
    }

    const ButtonConversation = () => {
        setViewConversation(true);
    }

    const ButtonCompressed = () => {
        setViewCompressed(true);
    }

    const ButtonBack = () => {
        setWorkspaceItem({});
        Dialog({view: 'dashboardWorkspace', tab: 'workspace'});
    }

    const InputScreen = async () => {
        if (capturing) return;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
            setToast({
                message: locale.dashboard.c,
                color: 'danger',
                display: 'bottom',
                duration: 3000
            });
            return;
        }

        setCapturing(true);

        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    mediaSource: 'screen',
                    cursor: 'always'
                },
                audio: false
            });

            const video = document.createElement('video');
            video.srcObject = stream;
            video.autoplay = true;

            await new Promise((resolve) => {
                video.onloadedmetadata = resolve;
            });

            await new Promise(resolve => setTimeout(resolve, 100));

            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            stream.getTracks().forEach(track => track.stop());

            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.addEventListener('load', () => {
                        const itemData = reader.result;
                        const now = new Date();
                        const timestamp = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}@${now.getHours()}:${now.getMinutes()}`;
                        const itemName = `Screen:${timestamp}.png`;
                        const itemType = 'image/png';

                        const message = {
                            type: 'wss',
                            path: 'workspace-file',
                            action: 'put',
                            data: {
                                file: itemData,
                                name: itemName,
                                type: itemType,
                                teamtalk: (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id, users: teamtalk.users} : null,
                                tab: (!Empty(teamtalk) && teamtalk.id) ? null : navigation.tab
                            }
                        };
                        wsCall(message);

                        setToast({
                            message: locale.dashboard.e,
                            color: 'info',
                            display: 'bottom',
                            duration: 1000
                        });
                    });
                    reader.readAsDataURL(blob);
                } else {
                    throw new Error('Failed to create image from canvas');
                }
            }, 'image/png', 0.95);

        } catch (error) {
            if (error.name === 'NotAllowedError') {
                setToast({
                    message: locale.dashboard.f,
                    color: 'warning',
                    display: 'bottom',
                    duration: 3000
                });
            } else {
                console.error('Screenshot capture error:', error);
                setToast({
                    message: locale.dashboard.g,
                    color: 'danger',
                    display: 'bottom',
                    duration: 3000
                });
            }
        } finally {
            setCapturing(false);
        }
    }

    const Workspace = useCallback(() => {

        const message = {
            type: 'wss',
            path: 'workspace',
            action: 'get',
            data: {
                teamtalk:  (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id} : null,
                tab: (!Empty(teamtalk) && teamtalk.id) ? null : navigation.tab
            }
        };
        wsCall(message);
    }, [wsCall, teamtalk, navigation.tab]);

    const GetUserLogin = useCallback(data => {
        if (data.mobiFunctions) {
            let item = {...configuration};
            item.mobi.functions = data.mobiFunctions;
            setConfiguration(item);
        }
        if (data.ui) {
            setUi(data.ui);
        }
        if (data.tab) {
            Navigation({tab: data.tab});
        }
        if (data.thinking) {
            setThinking(data.thinking);
        }
        if (data.locked) {
            setLocked(true);
        }
        if (data.model) {
            setModel(data.model);
        }
        if (data.voice) {
            setVoice(data.voice);
        }
        if (data.avatar) {
            setAvatar(data.avatar);
        }
        if (data.label) {
            setLabel(data.label);
        }
        if (data.messages) {
            setMessages(data.messages);
        }
        if (data.messages && data.messages.length !== 0) {
            setFolded(true);
        } else if (data.messages && data.messages.length === 0) {
            setFolded(false);
        }
        if (data.messages && data.messages.length !== 0 && data.messages[0].usage && data.messages[0].usage.tokensTotal) {
            setTokensUsed(data.messages[0].usage.tokensTotal);
        } else {
            setTokensUsed(0);
        }
        if (data.functions) {
            setFunctions(data.functions);
        }
        if (window.localStorage.text) {
            setText(window.localStorage.text);
        }
        if (data.user) {
            setUser(data.user);
            Locale(data.user.locale);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.notifications) {
            data.notifications = data.notifications.reverse();
            setNotifications(data.notifications);
        }
        if (data.session) {
            session.current.id = data.session;
        }
        setLoading(false);
    }, [configuration, Locale, Navigation, Dialog, sort]);

    const GetConversation = useCallback(data => {
        if (data.model) {
            setModel(data.model);
        }
        if (data.messages) {
            setMessages(data.messages);
        }
        if (data.messages && data.messages.length !== 0) {
            setFolded(true);
        } else if (data.messages && data.messages.length === 0) {
            setFolded(false);
        }
        if (data.avatar) {
            setAvatar(data.avatar);
        } else {
            setAvatar(null);
        }
        if (data.voice) {
            setVoice(data.voice);
        }
        setViewConversation(false);
        setViewCompressed(false);
        if (data.messages && data.messages.length !== 0 && data.messages[0].usage && data.messages[0].usage.tokensTotal) {
            setTokensUsed(data.messages[0].usage.tokensTotal);
        } else {
            setTokensUsed(0);
        }
        if (data.thinking) {
            setThinking(data.thinking);
        } else {
            setThinking(null);
        }
        if (data.locked) {
            setLocked(true);
        } else {
            setLocked(false);
        }
        if (data.functions) {
            setFunctions(data.functions);
        }
        if (data.tab) {
            Navigation({tab: data.tab});
        }
        if (data.ui) {
            setUi(data.ui);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (dialog.view && !data.locked) {
            Dialog({view: 'close'});
        }
    }, [Dialog, dialog.view, Navigation, sort]);

    const GetTab = useCallback(data => {
        if (window.localStorage.text !== text) {
            setText(window.localStorage.text);
        }
        if (data.model) {
            setModel(data.model);
        }
        if (data.voice) {
            setVoice(data.voice);
        }
        if (data.label) {
            setLabel(data.label);
        }
        if (data.thinking) {
            setThinking(data.thinking);
        } else {
            setThinking(null);
        }
        if (data.locked) {
            setLocked(true);
        } else {
            setLocked(false);
        }
        if (data.avatar) {
            setAvatar(data.avatar);
        } else {
            setAvatar(null);
        }
        if (data.messages) {
            setMessages(data.messages);
        }
        if (data.messages && data.messages.length !== 0) {
            setFolded(true);
        } else if (data.messages && data.messages.length === 0) {
            setFolded(false);
        }
        setViewConversation(false);
        setViewCompressed(false);
        if (data.messages && data.messages.length !== 0 && data.messages[0].usage && data.messages[0].usage.tokensTotal) {
            setTokensUsed(data.messages[0].usage.tokensTotal);
        } else {
            setTokensUsed(0);
        }
        if (data.functions) {
            setFunctions(data.functions);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.tab) {
            Navigation({tab: data.tab});
        }
        setWorkspaceItem({});
        setLoading(false);
        setViewConversation(false);
        setBroadcast(null);
    }, [Navigation, text, sort]);

    const GetTeamtalkConversation = useCallback(data => {
        if (data.model) {
            setModel(data.model);
        }
        if (data.messages) {
            setMessages(data.messages);
        }
        if (data.messages && data.messages.length !== 0) {
            setFolded(true);
        } else if (data.messages && data.messages.length === 0) {
            setFolded(false);
        }
        setViewConversation(false);
        setViewCompressed(false);
        if (data.functions) {
            setFunctions(data.functions);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.teamtalk) {
            setTeamtalk(data.teamtalk);
        }
        if (data.question) {
            setText(data.question);
        }
        if (navigation.tab) {
            Navigation({tab: 'teamtalk'});
        }
        setWorkspaceItem({});
        setViewConversation(false);
    }, [Navigation, navigation.tab, sort]);

    const DeleteTab = useCallback(data => {
        if (data.model) {
            setModel(data.model);
        }
        if (data.messages) {
            setMessages(data.messages);
        }
        if (data.avatar) {
            setAvatar(data.avatar);
        } else {
            setAvatar(null);
        }
        if (data.voice) {
            setVoice(data.voice);
        }
        if (data.label) {
            setLabel(data.label);
        }
        if (data.messages && data.messages.length !== 0) {
            setFolded(true);
        } else if (data.messages && data.messages.length === 0) {
            setFolded(false);
        }
        setViewConversation(false);
        setViewCompressed(false);
        if (data.messages && data.messages.length !== 0 && data.messages[0].usage && data.messages[0].usage.tokensTotal) {
            setTokensUsed(data.messages[0].usage.tokensTotal);
        } else {
            setTokensUsed(0);
        }
        if (data.functions) {
            setFunctions(data.functions);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.tab) {
            Navigation({tab: data.tab});
        }
        setViewConversation(false);
    }, [Navigation, sort]);

    const BroadcastMessage = useCallback(data => {
        if (data.message) {
            //setBroadcast(HTMLPlain(data.message));
            setBroadcast(data.message);
        }
    }, []);

    const DeleteConversationItem = useCallback(data => {
        if (data.messages) {
            setMessages(data.messages);
        }
    }, []);

    const UpdateAction = useCallback(data => {
        if (data.toast) {
            Workspace();
        }
    }, [Workspace]);

    const UpdateNotification = useCallback(data => {
        if (data.text) {
            alert({
                title: locale.dashboard.n,
                message: data.text,
                display: 'center'
            })
        }
    }, [locale.dashboard.n]);

    const UpdateWorkspaceItem = useCallback(data => {
        if (data.item) {
            setWorkspaceItem(data.item);
        }
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        setLoading(false);
    }, [sort]);

    const GetWorkspaceFile = useCallback(data => {
        if (data.item) {
            let dataUrl;

            if (data.item.data.type === 'image') {
                const image = "data:" + data.item.data.mime + ";base64," + data.item.file;

                const downloadLink = document.createElement('a');
                downloadLink.href = image;
                downloadLink.download = data.item.name + '.' + data.item.data.ext;
                downloadLink.style.display = 'none';

                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                URL.revokeObjectURL(image);
            } else if (data.item.data.subType === 'pdf') {
                const base64String = data.item.file;
                const binaryString = atob(base64String);
                const bytes = new Uint8Array(binaryString.length);

                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const blob = new Blob([bytes], { type: data.item.data.mime });
                dataUrl = URL.createObjectURL(blob);

            } else if (data.item.data.type === 'text') {

                const blob = new Blob([data.item.file], { type: data.item.data.mime });
                dataUrl = URL.createObjectURL(blob);

            }

            if (data.item.data.subType === 'pdf' || data.item.data.type === 'text') {
                const downloadLink = document.createElement('a');
                downloadLink.href = dataUrl;
                downloadLink.download = data.item.name + '.' + data.item.data.ext;
                downloadLink.style.display = 'none';

                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            }
        }
    }, []);

    const UpdateItemName = useCallback(data => {
        if (data.toast) {
            setMyItem({});
            Dialog({view: 'dashboardWorkspace', tab: 'items'});
        }
    }, [Dialog]);

    const PutWorkspaceFile = useCallback(data => {
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.error) {
            mbToast({message: locale.dashboard.t, color: 'danger', display: 'bottom', duration: 3000});
        } else {
            mbToast({message: locale.dashboard.h, color: 'info', display: 'bottom', duration: 1000});
        }
    }, [locale.dashboard.h, locale.dashboard.t, sort]);

    const PutWorkspaceFileGenerate = useCallback(data => {
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.toast){
            mbToast({message: locale.dashboard.h, color: 'info', display: 'bottom', duration: 1000});
        }
        Dialog({view: 'dashboardWorkspace', tab: 'workspace'});
    }, [locale.dashboard.h, Dialog, sort]);

    const UpdateWorkspaceName = useCallback(data => {
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        setWorkspaceItemDialog({});
        Dialog({view: 'dashboardWorkspace', tab: 'workspace'});
    }, [Dialog, sort]);

    const PutWorkspaceItem = useCallback(data => {
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        setWorkspaceItemDialog({});
        Dialog({view: 'dashboardWorkspace', tab: 'workspace'});
    }, [Dialog, sort]);

    const GetWorkspace = useCallback(data => {
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
        }
        if (data.notifications) {
            data.notifications = data.notifications.reverse();
            setNotifications(data.notifications);
        }
    }, [sort]);

    const BroadcastTeamtalkWorkspace = useCallback(data => {
        if (data.workspace) {
            const workspaceItems = data.workspace.filter(item => !item.stacked || item.stacked === false).sort((a, b) => sort ? (Number(b.id) - Number(a.id)) : (Number(a.id) - Number(b.id)));
            const array = data.workspace.filter(item => item.stacked === true);
            const stackItems = array.map((item, index) => ({...item, stackClass: index}));

            setWorkspace(workspaceItems);
            setStack(stackItems);
            mbToast({message: locale.dashboard.o, color: 'info', display: 'bottom', duration: 1000});
        }
        if (data.workspace && !data.item && !Empty(workspaceItem) && !data.workspace.find(item => item.id === workspaceItem.id)) {
            setWorkspaceItem({});
        }
        if (data.item && !Empty(workspaceItem) && workspaceItem.id === data.item.id) {
            setWorkspaceItem(data.item);
        }
    }, [locale.dashboard.o, workspaceItem, sort]);

    const BroadcastReconnect = useCallback(data => {
        if (data.toast) {

        }
    }, []);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'broadcast' &&
            wsResponse.path === 'teamtalk-workspace' &&
            !Empty(teamtalk) &&
            wsResponse.data.teamtalk &&
            teamtalk.id === wsResponse.data.teamtalk) {
            BroadcastTeamtalkWorkspace(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, BroadcastTeamtalkWorkspace, setWsResponse, teamtalk]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'update' && wsResponse.path === 'workspace-item') {
            UpdateWorkspaceItem(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateWorkspaceItem, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'update' && wsResponse.path === 'item-name') {
            UpdateItemName(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateItemName, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'put' && wsResponse.path === 'workspace-file-generate') {
            PutWorkspaceFileGenerate(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, PutWorkspaceFileGenerate, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'put' && wsResponse.path === 'workspace-file') {
            PutWorkspaceFile(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, PutWorkspaceFile, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'update' && wsResponse.path === 'workspace-name') {
            UpdateWorkspaceName(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateWorkspaceName, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'put' && wsResponse.path === 'workspace-item') {
            PutWorkspaceItem(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, PutWorkspaceItem, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'workspace-file') {
            GetWorkspaceFile(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, GetWorkspaceFile, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'workspace') {
            GetWorkspace(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, GetWorkspace, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'update' && wsResponse.path === 'action') {
            UpdateAction(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateAction, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'update' && wsResponse.path === 'notification') {
            UpdateNotification(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateNotification, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'delete' && wsResponse.path === 'conversation-item') {
            DeleteConversationItem(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, DeleteConversationItem, setWsResponse]);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'broadcast' &&
            wsResponse.path === 'message' &&
            ((Empty(teamtalk) && !wsResponse.data.teamtalk && navigation.tab === wsResponse.data.tab) ||
                (!Empty(teamtalk) && wsResponse.data.teamtalk && teamtalk.id === wsResponse.data.teamtalk))) {
            BroadcastMessage(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, BroadcastMessage, setWsResponse, teamtalk, navigation.tab]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'conversation') {
            GetConversation(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, GetConversation, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'delete' && wsResponse.path === 'tab') {
            DeleteTab(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, DeleteTab, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'tab') {
            GetTab(wsResponse.data);
            setWsResponse({});
            setToast({});
        }
    }, [wsResponse, GetTab, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'teamtalk-conversation') {
            GetTeamtalkConversation(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, GetTeamtalkConversation, setWsResponse]);

    useEffect(() => {
        if (wsResponse && wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'user-login') {
            GetUserLogin(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, GetUserLogin, setWsResponse]);

    useEffect(() => {
        if (wsResponse && wsResponse && !Empty(wsResponse) && wsResponse.action === 'broadcast' && wsResponse.path === 'reconnect') {
            BroadcastReconnect(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, GetUserLogin, setWsResponse]);

    useEffect(() => {
        Start();
    }, []);

  return (
      <>
      {!loading && !Empty(user) &&
          <DashboardHeader
              wss={wss}
              width={width}
              locale={locale}
              dialog={dialog}
              Dialog={Dialog}
              configuration={configuration}
              user={user}
              Conversation={Conversation}
              functions={functions}
              toast={toast}
              system={system}
              Navigation={Navigation}
              navigation={navigation}
              setMessages={setMessages}
              setLocale={setLocale}
              setUser={setUser}
              token={token}
              setToast={setToast}
              setText={setText}
              setWorkspace={setWorkspace}
              setStack={setStack}
              teamtalk={teamtalk}
              setTeamtalk={setTeamtalk}
              notifications={notifications}
              setNotifications={setNotifications}
              account={account}
              setTokensUsed={setTokensUsed}
              setFolded={setFolded}
              sort={sort}
              label={label}
              setLabel={setLabel}
          />
      }
          <div className={width > 576 ? "mbsc-grid cs-dashboard-grid" : "mbsc-grid cs-dashboard-grid-small"} >
              {loading &&
                  <Loading />
              }
              {!loading && !Empty(user) && configuration.billingStatus === 'error' &&
                  <div className="mbsc-row mbsc-justify-content-center cs-dashboard-section">
                      <div className="mbsc-bold mbsc-padding mbsc-txt-muted mbsc-txt-m">
                          {locale.dashboard.p}
                      </div>
                  </div>
              }
              {!loading && !Empty(user) && configuration.billingStatus === 'active' &&
                  <DashboardQuestion
                      width={width}
                      configuration={configuration}
                      Dialog={Dialog}
                      dialog={dialog}
                      toast={toast}
                      system={system}
                      locale={locale}
                      setModel={setModel}
                      model={model}
                      functions={functions}
                      setFunctions={setFunctions}
                      text={text}
                      setText={setText}
                      setToast={setToast}
                      setMessages={setMessages}
                      Navigation={Navigation}
                      messages={messages.length}
                      setSystem={setSystem}
                      setSnackbar={setSnackbar}
                      setWorkspace={setWorkspace}
                      workspace={workspace.length}
                      stack={stack.length}
                      setStack={setStack}
                      navigation={navigation}
                      teamtalk={teamtalk}
                      setTeamtalk={setTeamtalk}
                      Conversation={Conversation}
                      user={user}
                      setThinking={setThinking}
                      setViewConversation={setViewConversation}
                      tokensUsed={tokensUsed}
                      setTokensUsed={setTokensUsed}
                      folded={folded}
                      setFolded={setFolded}
                      sort={sort}
                      setAvatar={setAvatar}
                      setBroadcast={setBroadcast}
                      setLocked={setLocked}
                      setLabel={setLabel}
                  />
              }
              {!loading && navigation.card === 'calendar' && configuration.billingStatus === 'active' &&
                  <DashboardCalendar
                      locale={locale}
                      Navigation={Navigation}
                      navigation={navigation}
                      width={width}
                      calendar={calendar}
                      setCalendar={setCalendar}
                      Dialog={Dialog}
                      dialog={dialog}
                      setToast={setToast}
                      ui={ui}
                      setUi={setUi}
                  />
              }
              {dialog.view === 'dashboardWorkspace' && configuration.billingStatus === 'active' &&
                  <DashboardWorkspace
                      locale={locale}
                      navigation={navigation}
                      width={width}
                      setToast={setToast}
                      workspace={workspace}
                      stack={stack}
                      setStack={setStack}
                      toast={toast}
                      Dialog={Dialog}
                      setWorkspace={setWorkspace}
                      dialog={dialog}
                      teamtalk={teamtalk}
                      setWorkspaceItem={setWorkspaceItem}
                      sort={sort}
                      setSort={setSort}
                      setWorkspaceItemDialog={setWorkspaceItemDialog}
                      hiddenFileInput={hiddenFileInput}
                      InputScreen={InputScreen}
                      setMyItem={setMyItem}
                  />
              }
              {!loading && (thinking || locked) && configuration.billingStatus === 'active' &&
                  <DashboardThinking
                      locale={locale}
                      thinking={thinking}
                      voice={voice}
                      avatar={avatar}
                      broadcast={broadcast}
                      setToast={setToast}
                      locked={locked}
                      Dialog={Dialog}
                  />
              }
              {!loading && !thinking && configuration.billingStatus === 'active' && messages && messages.length !== 0 && messages.map((item, index) => {
                  return (
                      <DashboardConversation
                          width={width}
                          locale={locale}
                          configuration={configuration}
                          item={item}
                          user={user}
                          system={system}
                          Delete={Delete}
                          index={index}
                          viewConversation={viewConversation}
                          viewWorkspace={navigation.card === 'workspace'}
                          thinking={!!thinking}
                          viewCompressed={viewCompressed}
                          avatar={avatar}
                          voice={voice}
                      />
                  );
              })}
              {!loading && !thinking && !viewConversation && configuration.billingStatus === 'active' && messages && messages.length > 1 &&
                  <div className="mbsc-row cs-dashboard-view">
                      <div className="mbsc-col mbsc-align-center">
                          <Button onClick={() => ButtonConversation()} color="primary" variant="flat" className="mbsc-bold cs-dashboard-color" >
                              <div className="mbsc-txt-muted">
                                  {locale.dashboard.q}{messages.length - 1} {messages.length === 2 ? locale.dashboard.s : locale.dashboard.r}
                              </div>
                          </Button>
                      </div>
                  </div>
              }
              {!loading && !thinking && viewConversation && !viewCompressed && configuration.billingStatus === 'active' && messages && messages.length > 1 && messages.some(item => item.compressed) &&
                  <div className="mbsc-row cs-dashboard-view">
                      <div className="mbsc-col mbsc-align-center">
                          <Button onClick={() => ButtonCompressed()} color="primary" variant="flat" className="mbsc-bold cs-dashboard-color" >
                              <div className="mbsc-txt-muted">
                                  {locale.dashboard.d}{messages.filter(m => m.compressed).length - 1} {messages.filter(m => m.compressed).length === 2 ? locale.dashboard.s : locale.dashboard.r}
                              </div>
                          </Button>
                      </div>
                  </div>
              }
          </div>
          <Snackbar
              isOpen={!Empty(snackbar)}
              onClose={() => setSnackbar({})}
              message={snackbar.message}
              duration={snackbar.duration}
              color={snackbar.color}
              display={snackbar.display}
              button={{
                  text: locale.dashboard.i,
                  action: () => {
                      setSnackbar({})
                  }
              }}
          />
          <Toast
              isOpen={!Empty(toast)}
              message={toast.message}
              duration={toast.duration}
              color={toast.color}
              display={toast.display}
              onClose={() => setToast({})}
          />
          {!loading && !Empty(workspaceItem) && workspaceItem.type === 'graph' &&
              <DashboardWorkspaceGraph
                  locale={locale}
                  workspaceItem={workspaceItem}
                  setLoading={setLoading}
                  teamtalk={teamtalk}
                  navigation={navigation}
                  ButtonBack={ButtonBack}
                  width={width}
              />
          }
          {!loading && !Empty(workspaceItem) && workspaceItem.type === 'text' && workspaceItem.subtitle === 'document' &&
              <DashboardWorkspaceDoc
                  locale={locale}
                  workspaceItem={workspaceItem}
                  setLoading={setLoading}
                  teamtalk={teamtalk}
                  navigation={navigation}
                  ButtonBack={ButtonBack}
                  width={width}
              />
          }
          {!loading && !Empty(workspaceItem) && workspaceItem.type === 'text' && workspaceItem.subtitle === 'editor' &&
              <DashboardWorkspaceText
                  locale={locale}
                  workspaceItem={workspaceItem}
                  setLoading={setLoading}
                  teamtalk={teamtalk}
                  navigation={navigation}
                  ButtonBack={ButtonBack}
                  width={width}
              />
          }
          {!loading && !Empty(workspaceItem) && workspaceItem.type === 'text' && workspaceItem.subtitle === 'code' &&
              <DashboardWorkspaceCode
                  locale={locale}
                  workspaceItem={workspaceItem}
                  setLoading={setLoading}
                  teamtalk={teamtalk}
                  navigation={navigation}
                  ButtonBack={ButtonBack}
                  width={width}
              />
          }
          {!loading && !Empty(workspaceItem) && workspaceItem.type === 'app' &&
              <DashboardWorkspaceApp
                  locale={locale}
                  workspaceItem={workspaceItem}
                  ButtonBack={ButtonBack}
                  teamtalk={teamtalk}
                  width={width}
                  setWorkspaceItem={setWorkspaceItem}
              />
          }
          {!loading && !Empty(workspaceItem) && workspaceItem.type === 'file' && workspaceItem.subtitle && workspaceItem.subtitle.toLowerCase().includes('image') &&
              <DashboardWorkspaceImage
                  locale={locale}
                  workspaceItem={workspaceItem}
                  setLoading={setLoading}
                  teamtalk={teamtalk}
                  navigation={navigation}
                  ButtonBack={ButtonBack}
                  width={width}
                  setToast={setToast}
                  setWorkspaceItem={setWorkspaceItem}
              />
          }
          {!Empty(workspaceItem) && dialog.view === 'dashboardWorkspaceEvent' &&
              <DashboardWorkspaceEvent
                  Dialog={Dialog}
                  dialog={dialog}
                  workspaceItem={workspaceItem}
                  setWorkspaceItem={setWorkspaceItem}
                  locale={locale}
                  width={width}
                  setLoading={setLoading}
                  navigation={navigation}
              />
          }
          {dialog.view === 'dashboardWorkspaceAddName' &&
              <DashboardWorkspaceAddName
                  dialog={dialog}
                  Dialog={Dialog}
                  locale={locale}
                  width={width}
                  workspaceItemDialog={workspaceItemDialog}
                  teamtalk={teamtalk}
                  navigation={navigation}
              />
          }
          {dialog.view === 'dashboardWorkspaceRename' &&
              <DashboardWorkspaceRename
                  Dialog={Dialog}
                  dialog={dialog}
                  locale={locale}
                  width={width}
                  workspaceItemDialog={workspaceItemDialog}
                  teamtalk={teamtalk}
                  navigation={navigation}
                  setWorkspaceItemDialog={setWorkspaceItemDialog}
              />
          }
          {dialog.view === 'dashboardItemsRename' &&
              <DashboardItemsRename
                  Dialog={Dialog}
                  dialog={dialog}
                  locale={locale}
                  width={width}
                  myItem={myItem}
                  setMyItem={setMyItem}
              />
          }
          {dialog.view === 'dashboardWorkspaceGenerate' &&
              <DashboardWorkspaceGenerate
                  Dialog={Dialog}
                  dialog={dialog}
                  locale={locale}
                  setToast={setToast}
                  toast={toast}
                  width={width}
                  setWorkspaceItemDialog={setWorkspaceItemDialog}
                  user={user}
                  navigation={navigation}
                  teamtalk={teamtalk}
              />
          }
      </>
  );
}

export default Dashboard;
